import { isJsonObject } from './bytes';
import {
  DocumentLockedError,
  KeyRefusedError,
  KeyResponseInvalidError,
  KeyServiceUnavailableError,
} from './errors';
import { startSessionHeartbeat } from './heartbeat';
import { EXPIRED } from './heartbeatDeadlines';
import {
  idTokenOrSignIn,
  isDenyBody,
  isFiniteNumber,
  keyServiceDepsOf,
  keyServiceError,
  lockGraceSecondsOrZero,
  postJson,
  readJson,
  requireString,
  type TableViewRequestBody,
} from './keyApi';
import { withinKeyServiceTimeout } from './requestTimeout';

import type {
  AcceptedPiiNoticeVersion,
  KeyServiceDeps,
  LockReason,
  OpenedTableView,
  TableViewDeps,
  TableViewGrant,
} from './types';

const UNCONFIRMED: LockReason = { kind: 'unreachable' };

export interface TableViewRequestOptions {
  piiNoticeVersion?: AcceptedPiiNoticeVersion;
  signal?: AbortSignal;
}

export async function requestTableView(
  viewId: string,
  deps: KeyServiceDeps,
  { piiNoticeVersion, signal }: TableViewRequestOptions = {},
): Promise<TableViewGrant> {
  const body: TableViewRequestBody = { viewId };
  if (piiNoticeVersion !== undefined) {
    body.piiNoticeVersion = piiNoticeVersion;
  }
  const idToken = await idTokenOrSignIn(deps);
  signal?.throwIfAborted();
  let status: number;
  let payload: unknown;
  try {
    const response = await postJson(deps, '/view', idToken, body, signal);
    status = response.status;
    payload = await readJson(response);
  } catch {
    signal?.throwIfAborted();
    throw new KeyServiceUnavailableError();
  }
  if (isDenyBody(payload)) throw new KeyRefusedError(payload.reason);
  if (status !== 200) throw keyServiceError(status, payload);
  return parseTableViewGrant(payload, viewId);
}

function parseTableViewGrant(payload: unknown, viewId: string): TableViewGrant {
  if (!isJsonObject(payload) || payload.allow !== true) {
    throw new KeyResponseInvalidError('allow');
  }
  if (payload.viewId !== viewId) {
    throw new KeyResponseInvalidError('viewId');
  }
  const jti = requireString(payload, 'jti');
  if (jti === '') {
    throw new KeyResponseInvalidError('jti');
  }
  const { secondsRemaining, level } = payload;
  if (!isFiniteNumber(secondsRemaining) || secondsRemaining < 0) {
    throw new KeyResponseInvalidError('secondsRemaining');
  }
  if (level !== 1 && level !== 2) {
    throw new KeyResponseInvalidError('level');
  }
  return {
    viewId,
    level,
    docName: requireString(payload, 'docName'),
    docScope: requireString(payload, 'docScope'),
    viewerName: requireString(payload, 'viewerName'),
    openedAt: requireString(payload, 'openedAt'),
    sourceIp: requireString(payload, 'sourceIp'),
    saleKey: requireString(payload, 'saleKey'),
    poolNumber: requireString(payload, 'poolNumber'),
    session: {
      jti,
      secondsRemaining,
      lockGraceSeconds: lockGraceSecondsOrZero(payload.lockGraceSeconds),
    },
  };
}

// The PII notice is the route's to show before it registers the view with api, so a
// pii_notice_required refusal here is final rather than a prompt to ask again.
export async function openTableView(
  viewId: string,
  deps: TableViewDeps,
  piiNoticeVersion?: AcceptedPiiNoticeVersion,
): Promise<OpenedTableView> {
  deps.signal?.throwIfAborted();
  const keyService = keyServiceDepsOf(deps);
  const grant = await withinKeyServiceTimeout(
    (signal) => requestTableView(viewId, keyService, { piiNoticeVersion, signal }),
    deps.signal,
  );
  const accessEndsAtWallMs = Date.now() + grant.session.secondsRemaining * 1000;

  let returnedToRoute = false;
  let ended = false;
  let lockedWhileOpening: LockReason | undefined;

  function end(): void {
    ended = true;
    heartbeat.stop();
  }
  function lock(why: LockReason): void {
    if (!returnedToRoute) {
      lockedWhileOpening ??= why;
      return;
    }
    if (ended) return;
    end();
    deps.onLock(new DocumentLockedError(why));
  }
  function suspend(): void {
    if (!returnedToRoute) lock(UNCONFIRMED);
    else if (!ended) deps.onSuspend();
  }
  function resume(): void {
    if (returnedToRoute && !ended) deps.onResume();
  }
  function confirmed(secondsRemaining: number): void {
    if (returnedToRoute && !ended) deps.onAccessEndsAt?.(Date.now() + secondsRemaining * 1000);
  }

  const heartbeat = startSessionHeartbeat(
    '/view/heartbeat',
    { viewId, jti: grant.session.jti },
    grant.session,
    keyService,
    { onLock: lock, onSuspend: suspend, onResume: resume, onConfirmed: confirmed },
    deps.signal,
  );
  if (Date.now() >= accessEndsAtWallMs) lock(EXPIRED);
  if (lockedWhileOpening) {
    end();
    throw new DocumentLockedError(lockedWhileOpening);
  }
  returnedToRoute = true;
  return {
    viewId: grant.viewId,
    level: grant.level,
    docName: grant.docName,
    docScope: grant.docScope,
    viewerName: grant.viewerName,
    openedAt: grant.openedAt,
    sourceIp: grant.sourceIp,
    saleKey: grant.saleKey,
    poolNumber: grant.poolNumber,
    jti: grant.session.jti,
    accessEndsAtWallMs,
    close: end,
  };
}
